import { Fragment, type ReactNode } from 'react';

import { cx } from '@/lib/cx';
import { type ClassificacaoPersonalizadaEntrada } from '@fiscoai/contracts';

import { GRUPO, pecasCfop } from './textos';

/** Código da frase (CFOP): fonte de dados, fundo afundado, como nas regras. */
function Codigo({ children, ativa }: { children: ReactNode; ativa: boolean }) {
  return (
    <span
      className={cx(
        'rounded-mini bg-superficie-afundada px-1 py-px font-mono text-sm tabular',
        ativa ? 'text-texto' : 'text-texto-suave',
      )}
    >
      {children}
    </span>
  );
}

function Destaque({ children, ativa }: { children: ReactNode; ativa: boolean }) {
  return (
    <strong className={cx('font-semibold', ativa ? 'text-texto' : 'text-texto-suave')}>
      {children}
    </strong>
  );
}

type Frase = Pick<ClassificacaoPersonalizadaEntrada, 'cfopEntrada' | 'grupo' | 'patrimonio'> & {
  ativa: boolean;
};

function Entrada({ c }: { c: Frase }) {
  const pecas = pecasCfop(c.cfopEntrada);
  if (pecas.tipo === 'MESMO') return <>Entrada com o mesmo final do CFOP do fornecedor</>;
  return (
    <>
      Entrada <Codigo ativa={c.ativa}>{pecas.final}</Codigo> para{' '}
      {pecas.cfops.length === 1 ? 'o CFOP' : 'os CFOPs'}{' '}
      {pecas.cfops.length === 0 && <span className="text-texto-fraco">…</span>}
      {pecas.cfops.map((cfop, i) => (
        <Fragment key={cfop}>
          {i > 0 && ', '}
          <Codigo ativa={c.ativa}>{cfop}</Codigo>
        </Fragment>
      ))}{' '}
      do fornecedor
    </>
  );
}

/**
 * A classificação escrita como frase: "Entrada x949 para os CFOPs 5949, 6949 do fornecedor ·
 * grupo Custo · sem patrimônio". Desativada, a frase inteira fica em texto suave.
 */
export function FraseClassificacao({ classificacao: c }: { classificacao: Frase }) {
  return (
    <p className={cx('leading-relaxed', c.ativa ? 'text-texto-suave' : 'text-texto-fraco')}>
      <Entrada c={c} /> · grupo <Destaque ativa={c.ativa}>{GRUPO[c.grupo].frase}</Destaque> ·{' '}
      {c.patrimonio ? <Destaque ativa={c.ativa}>com patrimônio</Destaque> : 'sem patrimônio'}
    </p>
  );
}
